import {
    Activity,
    CircleGauge,
    GitCompare,
    Home,
    LogOut,
    MonitorDot,
    Sparkles,
    Users,
    X,
    LineChart /*, Zap */,
} from 'lucide-react' // Zap removed - Scanner hidden from nav
import type React from 'react'
import { useAuth } from '../../auth/AuthContext'
import { useAIInsights } from '../../hooks/useAIInsights'
import { calculateAnalysis } from '../../lib/analysis-utils'
import { mapChainRowToOptionData } from '../../lib/data-utils'
// import { useApp } from "../../context/AppContext"; // Replaced
import { useMarketStore } from '../../services/store'
import { useSettingsStore } from '../../stores/settingsStore'
import { NavItem } from './NavItem'

interface MobileMenuProps {
    isOpen: boolean
    onClose: () => void
}

export const MobileMenu: React.FC<MobileMenuProps> = ({ isOpen, onClose }) => {
    const { signOut, userData: user } = useAuth()

    // PERF FIX: Granular selectors instead of full store
    const settingsSymbol = useSettingsStore(s => s.symbol)
    const settingsExpiry = useSettingsStore(s => s.expiry)
    const settingsRange = useSettingsStore(s => s.range)
    const settingsMode = useSettingsStore(s => s.mode)

    const symbolData = useMarketStore((state) => state.data[settingsSymbol])
    const ltp = useMarketStore((state) => state.indexLtp[settingsSymbol])

    const pseudoMarketData = {
        options: (symbolData || []).map((row) =>
            mapChainRowToOptionData(row, settingsSymbol, settingsExpiry || '', Date.now()),
        ),
        spotPrice: ltp || 0,
        timestamp: Date.now(),
        vix: 0,
    } as any

    const {
        isOpen: isAiModalOpen,
        response: aiRes,
        isGenerating,
        generateInsights,
        closeAiModal,
        AiModalComponent,
    } = useAIInsights()

    const handleGenerateAiInsights = async () => {
        if (!pseudoMarketData.options.length) return

        try {
            const analysis = calculateAnalysis(
                pseudoMarketData.options,
                settingsRange,
                pseudoMarketData.spotPrice,
                settingsMode,
                settingsSymbol,
            )

            generateInsights(analysis, pseudoMarketData, useSettingsStore.getState() as any, 'Home')
        } catch (error) {
            console.error('AI Generation Failed:', error)
        }
    }

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 z-1000 flex lg:hidden">
            {/* Backdrop */}
            <div
                className="fixed inset-0 bg-black/60 backdrop-blur-md animate-in fade-in duration-200"
                onClick={onClose}
            />

            {/* Sidebar */}
            <div className="relative w-[300px] bg-white/70 dark:bg-slate-900/80 backdrop-blur-3xl h-full shadow-2xl animate-in slide-in-from-left duration-300 flex flex-col z-210 border-r border-white/20 dark:border-white/10 ring-1 ring-white/30 dark:ring-white/10">
                <div className="p-4 border-b border-white/20 dark:border-white/10 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="w-8 h-8 bg-[#005451] rounded-lg flex items-center justify-center text-white shadow-lg shadow-[#005451]/20 ring-1 ring-[#005451]/50">
                            <Activity className="w-5 h-5 text-white" strokeWidth={3} />
                        </div>
                        <span className="font-black text-lg text-slate-800 dark:text-slate-100 tracking-tight">
                            Oracle
                        </span>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-white/50 dark:hover:bg-white/10 rounded-full text-slate-500 dark:text-slate-400 hover:text-rose-500 transition-colors"
                    >
                        <X size={20} strokeWidth={3} />
                    </button>
                </div>

                <div className="flex-1 overflow-y-auto py-4">
                    <div className="px-2 space-y-1">
                        <p className="px-4 text-[10px] font-black text-indigo-300 dark:text-indigo-400 uppercase tracking-widest mb-2">
                            AI Assistant
                        </p>
                        <button
                            onClick={handleGenerateAiInsights}
                            disabled={isGenerating || !pseudoMarketData.options.length}
                            className="w-full flex items-center gap-3 px-4 py-3 rounded-xl mb-4 bg-linear-to-r from-indigo-600 to-violet-600 text-white shadow-lg shadow-indigo-500/30 ring-1 ring-white/20 active:scale-95 transition-all text-xs font-bold uppercase tracking-wider"
                        >
                            {isGenerating ? (
                                <Sparkles size={16} className="animate-spin" />
                            ) : (
                                <Sparkles size={16} className="animate-pulse text-amber-300" />
                            )}
                            Generate AI Insight
                        </button>

                        <p className="px-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2 mt-6">
                            Navigation
                        </p>
                        <NavItem
                            to="/home"
                            icon={Home}
                            label="Home"
                            color="blue"
                            orientation="horizontal"
                            onClick={onClose}
                        />
                        <NavItem
                            to="/dashboard"
                            icon={CircleGauge}
                            label="Dashboard"
                            color="indigo"
                            orientation="horizontal"
                            onClick={onClose}
                        />
                        {/* SCANNER: Hidden from navigation - uncomment below to re-enable */}
                        {/* <NavItem
                            to="/scanner"
                            icon={Zap}
                            label="Scanner"
                            color="violet"
                            orientation="horizontal"
                            onClick={onClose}
                        /> */}
                        <p className="px-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2 mt-6">
                            Tools
                        </p>
                        <NavItem
                            to="/analysis"
                            icon={LineChart}
                            label="Analysis"
                            color="violet"
                            orientation="horizontal"
                            onClick={onClose}
                        />
                        <NavItem
                            to="/inspector"
                            icon={MonitorDot}
                            label="Inspector"
                            color="cyan"
                            orientation="horizontal"
                            onClick={onClose}
                        />
                        {user?.role === 'admin' && (
                            <NavItem
                                to="/users"
                                icon={Users}
                                label="User Management"
                                color="rose"
                                orientation="horizontal"
                                onClick={onClose}
                            />
                        )}
                        <p className="px-4 text-[10px] font-black text-slate-400 dark:text-slate-500 uppercase tracking-widest mb-2 mt-6">
                            Extra Pages
                        </p>
                        <NavItem
                            to="/crossover"
                            icon={GitCompare}
                            label="Crossover"
                            color="emerald"
                            orientation="horizontal"
                            onClick={onClose}
                        />
                        <NavItem
                            to="/variance"
                            icon={Activity}
                            label="Variance"
                            color="amber"
                            orientation="horizontal"
                            onClick={onClose}
                        />
                    </div>
                </div>

                <div className="p-4 border-t border-white/20 dark:border-white/10 bg-white/10 dark:bg-black/20 space-y-2 backdrop-blur-md">
                    <div className="flex items-center gap-3 p-2 rounded-xl bg-white/40 dark:bg-white/5 border border-white/40 dark:border-white/10 shadow-sm backdrop-blur-sm">
                        {user?.photoURL ? (
                            <img
                                src={user.photoURL}
                                alt="User"
                                className="w-10 h-10 rounded-full border border-gray-200 dark:border-gray-700"
                            />
                        ) : (
                            <div className="w-10 h-10 rounded-full bg-primary/10 dark:bg-primary/20 flex items-center justify-center text-primary font-bold">
                                {(user?.displayName || 'U').charAt(0).toUpperCase()}
                            </div>
                        )}
                        <div className="flex-1 min-w-0">
                            <h3 className="font-bold text-gray-800 dark:text-gray-100">
                                {user?.displayName || 'Trader'}
                            </h3>
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user?.email}</p>
                        </div>
                        <button
                            onClick={() => {
                                signOut()
                                onClose()
                            }}
                            className="p-2 hover:bg-red-50 dark:hover:bg-red-900/20 text-gray-400 dark:text-gray-500 hover:text-red-500 transition-colors flex items-center justify-center"
                            title="Logout"
                        >
                            <LogOut size={18} />
                        </button>
                    </div>
                    <button
                        onClick={() => {
                            if ('serviceWorker' in navigator) {
                                navigator.serviceWorker.getRegistrations().then((registrations) => {
                                    for (const registration of registrations) registration.unregister()
                                })
                            }
                            // Force cache bust with timestamp
                            window.location.href = `${window.location.pathname}?v=${Date.now()}`
                        }}
                        className="w-full py-2 text-xs font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500 hover:text-indigo-500 dark:hover:text-indigo-400 hover:bg-indigo-50/50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors flex items-center justify-center gap-2"
                    >
                        <Sparkles size={14} />
                        Hard Refresh (Clear Cache)
                    </button>
                </div>
            </div>

            {/* AI Insights Modal */}
            <AiModalComponent isOpen={isAiModalOpen} response={aiRes} onClose={closeAiModal} />
            {isAiModalOpen && !aiRes && (
                <div className="fixed inset-0 z-9999 flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
                    <div className="bg-white/90 backdrop-blur-xl p-6 rounded-2xl shadow-2xl flex flex-col items-center gap-4 animate-in zoom-in-95 duration-200 border border-white/20">
                        <div className="relative">
                            <div className="absolute inset-0 bg-purple-500 blur-xl opacity-20 animate-pulse"></div>
                            <Sparkles size={32} className="text-purple-600 animate-spin relative z-10" />
                        </div>
                        <div className="flex flex-col items-center">
                            <span className="font-bold text-gray-800 text-lg">Thinking Deeply...</span>
                            <span className="text-xs text-gray-500">Analyzing Market Structure & OI</span>
                        </div>
                    </div>
                </div>
            )}
        </div>
    )
}
